import { FilteredItemProps } from '@delmonicos/shared-design-system-react/src/components/filtered-item/filtered-item';
import { GetChannelsPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/inputs/get-channels-paginated.model';
import { GatewayChannelWithChargePointInfoModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/outputs/gateway-channel-with-charge-point-info.model';
import {
  ColumnDef,
  ColumnOrderState,
  VisibilityState,
  createColumnHelper,
} from '@tanstack/react-table';
import React from 'react';

import { ChargePointChannelOcppStatusFilterStateController } from '@app/feature/charge-point/charge-point-channel-ocpp-status-filter-state/charge-point-channel-ocpp-status-filter-state.controller';
import { ChargePointIsMaintenanceFilterStateController } from '@app/feature/charge-point/charge-point-is-maintenance-filter-state/charge-point-is-maintenance-filter-state.controller';
import { ChargePointIsOnlineFilterStateController } from '@app/feature/charge-point/charge-point-is-online-filter-state/charge-point-is-online-filter-state.controller';
import { CustomerFilterStateController } from '@app/feature/customer/customer-filter-state/customer-filter-state.controller';
import { LocationFilterStateController } from '@app/feature/location/location-filter-state/location-filter-state.controller';
import { NetworkFilterStateController } from '@app/feature/network/network-filter-state/network-filter-state.controller';
import { TariffFilterStateController } from '@app/feature/tariff/tariff-filter-state/tariff-filter-state.controller';

import { ChannelListLastMessageReceivedDateFilterStateController } from '../channel-list-last-message-received-date-filter-state/channel-list-last-message-received-date-filter-state.controller';
import { ChannelListChargePointIdentityColumn } from './channel-list-charge-point-identity.column';
import { ChannelListChargePointModelColumn } from './channel-list-charge-point-model.column';
import { ChannelListChargePointNameColumn } from './channel-list-charge-point-name.column';
import { ChannelListCustomerColumn } from './channel-list-customer.column';
import { ChannelListIsOnlineColumn } from './channel-list-is-online.column';
import { ChannelListLastMessageReceivedDateColumn } from './channel-list-last-message-received-date.column';
import { ChannelListLocationColumn } from './channel-list-location.column';
import { ChannelListNetworkColumn } from './channel-list-network.column';
import { ChannelListPhysicalSocketsColumn } from './channel-list-physical-sockets.column';
import { ChannelListQrCodeColumn } from './channel-list-qr-code.column';
import { ChannelListTariffColumn } from './channel-list-tariff.column';
import { ChannelListUpdatedAtColumn } from './channel-list-updated-at.column';

// /////////////////////////////////////////////////////////////////////////////
// SORTING
// /////////////////////////////////////////////////////////////////////////////

export const CHANNEL_LIST_SORT_FIELD: NonNullable<
  GetChannelsPaginatedModel['sortField']
> = 'createdAt';

export const CHANNEL_LIST_SORT_ORDER: NonNullable<
  GetChannelsPaginatedModel['sortOrder']
> = 1;

// /////////////////////////////////////////////////////////////////////////////
// FILTERING
// /////////////////////////////////////////////////////////////////////////////

type ResourceFilterType = Partial<
  Record<
    keyof GetChannelsPaginatedModel,
    {
      componentType: React.ComponentType<{
        value: any;
        onRemove: FilteredItemProps['onRemove'];
      }>;
    }
  >
>;

export const CHANNEL_LIST_FILTER: ResourceFilterType = {
  isOnline: {
    componentType: ChargePointIsOnlineFilterStateController,
  },
  isMaintenance: {
    componentType: ChargePointIsMaintenanceFilterStateController,
  },
  lastMessageReceivedDate: {
    componentType: ChannelListLastMessageReceivedDateFilterStateController,
  },
  channelOcppStatus: {
    componentType: ChargePointChannelOcppStatusFilterStateController,
  },
  customerId: {
    componentType: CustomerFilterStateController,
  },
  locationId: {
    componentType: LocationFilterStateController,
  },
  networkId: {
    componentType: NetworkFilterStateController,
  },
  tariffId: {
    componentType: TariffFilterStateController,
  },
} as const;

export const CHANNEL_LIST_FILTER_NAME_LIST = Object.keys(
  CHANNEL_LIST_FILTER,
) as Array<keyof typeof CHANNEL_LIST_FILTER>;

// /////////////////////////////////////////////////////////////////////////////
// DISPLAYING
// /////////////////////////////////////////////////////////////////////////////

export const channelListColumnHelper =
  createColumnHelper<GatewayChannelWithChargePointInfoModel>();

type ResourceColumnType = ColumnDef<
  GatewayChannelWithChargePointInfoModel,
  any
>[];

export const CHANNEL_LIST_COLUMN_LIST: ResourceColumnType = [
  channelListColumnHelper.accessor(
    'chargePointIdentity',
    ChannelListChargePointIdentityColumn,
  ),
  channelListColumnHelper.accessor('qrCode', ChannelListQrCodeColumn),
  channelListColumnHelper.accessor(
    'chargePointName',
    ChannelListChargePointNameColumn,
  ),
  channelListColumnHelper.accessor('location', ChannelListLocationColumn),
  channelListColumnHelper.accessor(
    'chargePointModel',
    ChannelListChargePointModelColumn,
  ),
  channelListColumnHelper.accessor(
    'physicalSockets',
    ChannelListPhysicalSocketsColumn,
  ),
  channelListColumnHelper.accessor('tariff', ChannelListTariffColumn),
  channelListColumnHelper.accessor('customer', ChannelListCustomerColumn),
  channelListColumnHelper.accessor('network', ChannelListNetworkColumn),
  channelListColumnHelper.accessor('updatedAt', ChannelListUpdatedAtColumn),
  channelListColumnHelper.accessor('isOnline', ChannelListIsOnlineColumn),
  channelListColumnHelper.accessor(
    'lastMessageReceivedDate',
    ChannelListLastMessageReceivedDateColumn,
  ),
];

export const CHANNEL_LIST_COLUMN_ID_LIST = CHANNEL_LIST_COLUMN_LIST.flatMap(
  (columnDef) => {
    if ('accessorKey' in columnDef) {
      return [columnDef.accessorKey];
    }

    if (columnDef.id) {
      return [columnDef.id];
    }

    return [];
  },
);

export const CHANNEL_LIST_COLUMN_ORDER = [...CHANNEL_LIST_COLUMN_ID_LIST];

export const CHANNEL_LIST_COLUMN_VISIBILITY = {
  ...CHANNEL_LIST_COLUMN_ID_LIST.reduce((columnVisibility, columnId) => {
    return { ...columnVisibility, [columnId]: true };
  }, {}),
  // Columns coming from the parent charge point, hidden by default —
  // surfaced via the column picker rather than shown out of the box.
  isOnline: false,
  lastMessageReceivedDate: false,
};

// /////////////////////////////////////////////////////////////////////////////
// OPTIONS
// /////////////////////////////////////////////////////////////////////////////

export interface ChannelListOptionsModel {
  columnVisibility: VisibilityState;
  columnOrder: ColumnOrderState;
}

export const CHANNEL_LIST_DEFAULT_OPTIONS: ChannelListOptionsModel = {
  columnVisibility: CHANNEL_LIST_COLUMN_VISIBILITY,
  columnOrder: CHANNEL_LIST_COLUMN_ORDER,
};

export const CHANNEL_LIST_COLUMN_LABEL_I18N_KEY: Record<string, string> = {
  chargePointIdentity: 'delmonicos-shared-sdk.outputs-charge-point.identity',
  qrCode: 'delmonicos-shared-sdk.outputs-channel.qr-code',
  chargePointName: 'delmonicos-shared-sdk.outputs-charge-point.name',
  location: 'delmonicos-shared-sdk.outputs-gateway-charge-point.location',
  chargePointModel: 'delmonicos-shared-sdk.outputs-charge-point.model',
  physicalSockets: 'delmonicos-shared-sdk.outputs-channel.physical-sockets',
  tariff: 'delmonicos-shared-sdk.outputs-gateway-charge-point.tariff',
  customer: 'delmonicos-shared-sdk.outputs-gateway-charge-point.customer',
  network: 'delmonicos-shared-sdk.outputs-gateway-charge-point.network',
  updatedAt: 'delmonicos-shared-sdk.outputs-base-with-timestamp.updated-at',
  isOnline: 'delmonicos-shared-sdk.outputs-charge-point.is-online',
  lastMessageReceivedDate:
    'delmonicos-shared-sdk.outputs-charge-point.last-message-received-date',
};
